<template>
	<div v-if="bannerSettings && !loading" class="pb-1 w-full">
		<component
			:is="bannerSettings.isClickable && bannerSettings.clickLink ? 'a' : 'div'"
			:href="bannerSettings.isClickable && bannerSettings.clickLink ? bannerSettings.clickLink : undefined"
			class="text-black gradient flex w-full justify-between px-2 py-2 md:py-3 md:px-8 xl:px-16"
			:style="{ backgroundColor: bannerSettings.backgroundColor || '#FFF5F5' }"
		>
			<!-- Desktop Layout -->
			<div
				class="hidden md:flex py-1 flex-grow pl-8 space-x-4 items-center justify-center"
				:class="bannerSettings.buttonPosition === 'left' ? 'flex-row' : 'flex-row-reverse'"
			>
				<component
					v-if="bannerSettings.showButton"
					:is="bannerSettings.buttonLink ? 'a' : 'span'"
					:href="bannerSettings.buttonLink || undefined"
					class="px-4 py-1 rounded-full font-semibold text-sm whitespace-nowrap"
					:class="bannerSettings.buttonPosition === 'left' ? '' : 'ml-2'"
					:style="{
						backgroundColor: bannerSettings.buttonBackgroundColor || '#F44631',
						color: bannerSettings.buttonTextColor || '#FFFFFF',
					}"
				>
					{{ bannerSettings.buttonText }}
				</component>
				<div class="font-medium" :style="{ color: bannerSettings.messageTextColor || '#374151' }">
					{{ bannerSettings.messageText }}
				</div>
			</div>

			<!-- Mobile Layout -->
			<div
				class="md:hidden py-2 flex flex-grow flex-col pl-6 items-center justify-center"
				:class="bannerSettings.buttonPositionMobile === 'above' ? 'flex-col' : 'flex-col-reverse'"
			>
				<component
					v-if="bannerSettings.showButton"
					:is="bannerSettings.buttonLink ? 'a' : 'span'"
					:href="bannerSettings.buttonLink || undefined"
					class="px-4 py-1 mb-1 rounded-full font-semibold text-xs whitespace-nowrap"
					:class="bannerSettings.buttonPositionMobile === 'above' ? 'mb-2' : 'mt-2'"
					:style="{
						backgroundColor: bannerSettings.buttonBackgroundColor || '#F44631',
						color: bannerSettings.buttonTextColor || '#FFFFFF',
					}"
				>
					{{ bannerSettings.buttonText }}
				</component>
				<div class="text-sm font-medium text-center" :style="{ color: bannerSettings.messageTextColor || '#374151' }">
					{{ bannerSettings.messageTextMobile || bannerSettings.messageText }}
				</div>
			</div>

			<button @click.stop.prevent="hideBanner" class="w-6 md:w-8 flex items-center" type="button" title="Close">
				<div class="m-auto items-center flex w-full justify-end">
					<div class="rotate-45 absolute bg-ihh-slate-gray group-hover:bg-ihh-white py-[1px] px-2"></div>
					<div class="-rotate-45 bg-ihh-slate-gray group-hover:bg-ihh-white py-[1px] px-2"></div>
				</div>
			</button>
		</component>
	</div>
</template>

<script>
import axios from 'axios'
import { port, apiDomain, useDevAPI } from '../../../../config/config/settings'

const isDev = import.meta.env.DEV
let api = useDevAPI ? `http://localhost:${port}` : apiDomain
if (!isDev) api = apiDomain

export default {
	name: 'Banner',
	inject: ['store'],
	data() {
		return {
			bannerSettings: null,
			loading: true,
		}
	},
	async mounted() {
		await this.fetchBannerSettings()
	},
	methods: {
		async fetchBannerSettings() {
			try {
				const response = await axios.get(`${api}/controllers/banner/active`)
				this.bannerSettings = response.data

				if (this.bannerSettings) {
					// Check if this banner was previously hidden
					const bannerData = window.localStorage.getItem('banner')
					if (bannerData) {
						try {
							const { bannerId, hiddenAt } = JSON.parse(bannerData)
							// If this specific banner was hidden, check if 24 hours have passed
							if (bannerId === this.bannerSettings.id) {
								const hoursSinceHidden = (Date.now() - hiddenAt) / (1000 * 60 * 60)
								if (hoursSinceHidden < 24) {
									// Still within 24 hours, keep it hidden
									this.bannerSettings = null
									this.store.hideBanner()
									return
								} else {
									// More than 24 hours, clear localStorage and show banner
									window.localStorage.removeItem('banner')
								}
							}
						} catch (e) {
							// If data is corrupted, continue to show banner
						}
					}

					// Auto-hide if expired
					if (this.bannerSettings?.autoHideAfterDate) {
						const hideDate = new Date(this.bannerSettings.autoHideAfterDate)
						const now = new Date()
						if (now >= hideDate) {
							this.bannerSettings = null
							return
						}
					}

					// Valid banner exists, show it
					this.store.showBannerIfActive()
				}
			} catch (error) {
				console.error('Error fetching banner settings:', error)
				this.bannerSettings = null
				this.store.hideBanner()
			} finally {
				this.loading = false
			}
		},
		hideBanner() {
			// Store the banner ID in localStorage so it stays hidden
			if (this.bannerSettings?.id) {
				window.localStorage.setItem(
					'banner',
					JSON.stringify({
						bannerId: this.bannerSettings.id,
						hiddenAt: Date.now(),
					}),
				)
			}
			this.bannerSettings = null
			this.store.hideBanner()
		},
	},
}
</script>

<style scoped></style>
